import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, CheckCircle, Target, Users, Award } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const AboutPreview = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const { t } = useTranslation()

  const industries = [
    t('about.industries.healthcare'),
    t('about.industries.government'),
    t('about.industries.tax'),
    t('about.industries.registers'),
    t('about.industries.procurement'),
    t('about.industries.budget'),
    t('about.industries.municipal'),
    t('about.industries.education'),
    t('about.industries.construction'),
    t('about.industries.transport'),
    t('about.industries.interaction'),
    t('about.industries.economy')
  ]

  const features = [
    {
      icon: Target,
      title: t('about.features.expertise.title'),
      description: t('about.features.expertise.description')
    },
    {
      icon: Users,
      title: t('about.features.teamwork.title'),
      description: t('about.features.teamwork.description')
    },
    {
      icon: Award,
      title: t('about.features.solutions.title'),
      description: t('about.features.solutions.description')
    }
  ]

  return (
    <section ref={ref} className="section-padding bg-white">
      <div className="container-max">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-6">
              <Award className="w-4 h-4 mr-2" />
              {t('about.title')}
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('about.headline')}
            </h2>

            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              {t('about.description')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {features.map((feature, index) => {
                const IconComponent = feature.icon
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.2 + index * 0.1, duration: 0.6 }}
                    className="text-center"
                  >
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <IconComponent className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </motion.div>
                )
              })}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              <Link to="/about" className="btn-primary inline-flex items-center justify-center w-full md:w-auto">
                {t('about.cta')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Right Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 border border-primary-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                {t('about.directions.title')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {industries.slice(0, 8).map((industry, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.3 + index * 0.05, duration: 0.4 }}
                    className="flex items-start space-x-3"
                  >
                    <CheckCircle className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{industry}</span>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="mt-6 p-4 bg-primary-600 rounded-lg text-white"
              >
                <p className="text-sm font-medium">
                  {t('about.experience.text')}
                </p>
              </motion.div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            <div className="absolute -bottom-4 -left-4 w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center">
              <Users className="w-4 h-4 text-white" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default AboutPreview
